import React, { useState } from 'react';
import { Send, Phone, Mail, MapPin, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { siteConfig } from '../../data/mock';
import { toast } from 'sonner';

const ContactSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Create mailto link with form data
    const subject = encodeURIComponent(`Security Inquiry - ${formData.service || 'General'}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\n` +
      `Email: ${formData.email}\n` +
      `Phone: ${formData.phone}\n` +
      `Service Needed: ${formData.service}\n\n` +
      `Message:\n${formData.message || 'No additional details provided.'}`
    );
    
    // Open mailto link
    window.location.href = `mailto:info@armedguardofficers.com?subject=${subject}&body=${body}`;
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    toast.success('Opening your email client...', {
      description: 'Please send the email to complete your inquiry.'
    });
    
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      message: ''
    });
    setIsSubmitting(false);
  };

  return (
    <section id="contact" className="bg-[#121212] py-20 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Content */}
          <div>
            <p className="text-[#00FFD1] text-sm font-medium tracking-wide uppercase mb-4">
              Contact Us
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white leading-tight mb-6">
              Get Your Free Security Consultation
            </h2>
            <p className="text-lg text-white/70 leading-relaxed mb-10">
              Ready to discuss your security needs? Fill out the form and one of our experts 
              will contact you within 24 hours to provide a customized security solution.
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center border border-[#00FFD1]/30">
                  <Phone className="w-5 h-5 text-[#00FFD1]" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Call us anytime</p>
                  <a href={`tel:${siteConfig.phone}`} className="text-white text-lg hover:text-[#00FFD1] transition-colors">
                    {siteConfig.phone}
                  </a>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center border border-[#00FFD1]/30">
                  <Mail className="w-5 h-5 text-[#00FFD1]" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Email us</p>
                  <a href={`mailto:${siteConfig.email}`} className="text-white text-lg hover:text-[#00FFD1] transition-colors">
                    {siteConfig.email}
                  </a>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center border border-[#00FFD1]/30">
                  <MapPin className="w-5 h-5 text-[#00FFD1]" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Service Area</p>
                  <p className="text-white text-lg">{siteConfig.address}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Form */}
          <div className="bg-black border border-white/10 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-white">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="John Smith"
                  className="bg-[#121212] border-white/20 text-white placeholder:text-white/40 rounded-none h-12 focus:border-[#00FFD1] focus:ring-[#00FFD1]"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@company.com"
                    className="bg-[#121212] border-white/20 text-white placeholder:text-white/40 rounded-none h-12 focus:border-[#00FFD1] focus:ring-[#00FFD1]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-white">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="(555) 123-4567"
                    className="bg-[#121212] border-white/20 text-white placeholder:text-white/40 rounded-none h-12 focus:border-[#00FFD1] focus:ring-[#00FFD1]"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="service" className="text-white">Service Needed *</Label>
                <Select value={formData.service} onValueChange={handleServiceChange}>
                  <SelectTrigger className="bg-[#121212] border-white/20 text-white rounded-none h-12 focus:border-[#00FFD1] focus:ring-[#00FFD1]">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/20">
                    <SelectItem value="commercial" className="text-white hover:bg-white/10">Commercial Property Security</SelectItem>
                    <SelectItem value="residential" className="text-white hover:bg-white/10">Residential Community Security</SelectItem>
                    <SelectItem value="events" className="text-white hover:bg-white/10">Event Security</SelectItem>
                    <SelectItem value="vip" className="text-white hover:bg-white/10">VIP / Executive Protection</SelectItem>
                    <SelectItem value="other" className="text-white hover:bg-white/10">Other Services</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-white">Additional Details</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your security needs..."
                  rows={4}
                  className="bg-[#121212] border-white/20 text-white placeholder:text-white/40 rounded-none resize-none focus:border-[#00FFD1] focus:ring-[#00FFD1]"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#00FFD1] text-black hover:bg-[#00FFD1]/10 hover:text-[#00FFD1] rounded-none py-6 text-lg font-medium transition-all duration-400 flex items-center justify-center gap-3"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Request Consultation
                    <Send className="w-5 h-5" />
                  </>
                )}
              </Button>

              <p className="text-white/40 text-sm text-center">
                Your information is protected and will never be shared with third parties.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
